import { createTheme } from '@mui/material';

/**
 * Bộ token lấy nguyên từ demo của BA:
 * `docs/ai-seo-copilot-admin-demo_20260803.html`.
 *
 * Demo là bản chốt về mặt thị giác, nên chỗ nào MUI mặc định lệch với demo thì
 * sửa theo demo — không sửa demo theo MUI.
 */
export const tokens = {
  ink: '#07253F',
  ink2: '#0F3A57',
  teal: '#0E7C7B',
  tealDark: '#0A5F5E',
  mint: '#2EC4B6',
  amber: '#F2A65A',
  amberText: '#96591A',
  amberBg: '#FDF3E7',
  rose: '#D1495B',
  roseText: '#A32F42',
  roseBg: '#FBEDEF',
  okText: '#0A6A62',
  okBg: '#E7F6F3',
  paper: '#EDF2F3',
  line: '#DBE6E8',
  line2: '#EAF1F2',
  text: '#16303F',
  muted: '#5F7683',
  faint: '#8CA1AB',
  rowAlt: '#FAFCFC',
  rowHover: '#F3F9F9',
} as const;

/**
 * Demo dùng IBM Plex Sans + Space Grotesk. Đã đổi sang Be Vietnam Pro cho cả hai
 * vai: bộ chữ này thiết kế riêng cho tiếng Việt nên dấu to hơn và đặt cao hơn,
 * đọc rõ ở cỡ nhỏ trong bảng. Chữ số và slug vẫn dùng mono để thẳng cột.
 */
export const DISPLAY = '"Be Vietnam Pro", "Segoe UI", system-ui, sans-serif';
const BODY = DISPLAY;
export const MONO = '"IBM Plex Mono", ui-monospace, Consolas, monospace';

/** Ngưỡng màu theo điểm — demo dùng 75 và 60, giữ đúng để bảng và trang chi tiết khớp nhau. */
export function scoreTone(score: number): 'ok' | 'warn' | 'bad' {
  if (score >= 75) return 'ok';
  if (score >= 60) return 'warn';
  return 'bad';
}

const TONE_HEX = { ok: tokens.teal, warn: tokens.amber, bad: tokens.rose } as const;

export function scoreHex(score: number): string {
  return TONE_HEX[scoreTone(score)];
}

/** Chiều cao thanh trên cùng — dùng cả cho AppBar và cho mốc dính của tiêu đề bảng. */
export const TOPBAR_H = 60;

const display = { fontFamily: DISPLAY, letterSpacing: '-0.01em' } as const;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: tokens.teal, dark: tokens.tealDark, contrastText: '#fff' },
    secondary: { main: tokens.mint, contrastText: tokens.ink },
    success: { main: tokens.teal, dark: tokens.okText, light: tokens.okBg },
    warning: { main: tokens.amber, dark: tokens.amberText, light: tokens.amberBg },
    error: { main: tokens.rose, dark: tokens.roseText, light: tokens.roseBg },
    background: { default: tokens.paper, paper: '#fff' },
    // `faint` (#8CA1AB) chỉ đạt tương phản 2,7:1 trên nền trắng nên không dùng cho
    // chữ — để dành cho đường viền và icon trang trí. Chữ mờ nhất vẫn phải là
    // `muted` (4,8:1) để đọc được ở cỡ nhỏ.
    text: { primary: tokens.text, secondary: tokens.muted, disabled: tokens.muted },
    divider: tokens.line,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: BODY,
    fontSize: 15,
    h1: display,
    h2: display,
    h3: display,
    h4: display,
    h5: { ...display, fontWeight: 700 },
    h6: { ...display, fontWeight: 600 },
    // Số liệu luôn dùng chữ số bề rộng cố định để các hàng thẳng cột.
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { WebkitFontSmoothing: 'antialiased' },
        // Con số nào cũng phải thẳng cột — khai báo một lần ở đây.
        'table, .num': { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: { borderColor: tokens.line, borderRadius: 14 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 9 } },
    },
    MuiTableCell: {
      styleOverrides: {
        // Tiêu đề bảng nền tối như demo: cột mốc thị giác rõ, dính khi cuộn.
        head: {
          backgroundColor: tokens.ink,
          color: '#fff',
          fontWeight: 600,
          fontSize: '13px',
          borderBottom: 'none',
          whiteSpace: 'nowrap',
          padding: '11px 12px',
        },
        // 14,5px thay vì 13,5px của demo: chữ tiếng Việt có dấu mất nét trước
        // chữ không dấu khi thu nhỏ, mà đây là bảng phải đọc chứ không phải xem.
        // Đệm ngang 12px thay vì 16px mặc định của MUI: 8 cột × 8px dư mỗi bên là
        // 128px, đủ để bảng tràn ra ngoài thẻ khi người dùng phóng to trình duyệt.
        body: { borderBottomColor: tokens.line2, fontSize: '14.5px', padding: '10px 12px' },
        // Tiêu đề bảng dính vào ngay dưới thanh trên cùng (cao 60px) chứ không
        // phải vào mép khung bảng — vùng cuộn là cả trang.
        stickyHeader: { top: TOPBAR_H, backgroundColor: tokens.ink },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: { tooltip: { backgroundColor: tokens.ink, fontSize: '12px' } },
    },
  },
});
